















































import { Vue, Component, Watch } from 'vue-property-decorator'
import { context, map, search } from '@/store/modules'
import debounce from 'lodash/debounce'
import intersectionWith from 'lodash/intersectionWith'
import { AbstractNode } from '@/orrm/model'
import { Layer } from '@/orrm/model/layer'
import { InfoReglementation } from '@/orrm/model/info-reglementation'
import { Actualite } from '@/orrm/model/actualite'
import { Entity } from '@/orrm/model/entity'
import LayerWrapper from '@/orrm/wrapper/layer'
import { WKT } from 'ol/format'
import { Feature } from 'ol'
import { Geometry } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import { Commune } from '@/orrm/model/commune'
import { FeatureLike } from 'ol/Feature'
import { CancelablePromise } from '@/utils/promises'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'

@Component
export default class SearchInput extends Vue {
  dialog = false
  search = ''
  selection: AbstractNode | null = null
  debounceSearch = debounce(this.cancelableSearch, 300)
  workingSearch: CancelablePromise<AbstractNode[]> | null = null
  cb: (() => void) = noop

  get loading (): boolean {
    return search.loading
  }

  get items (): AbstractNode[] {
    return ([...search.results, this.selection].filter((item: AbstractNode | null) => item !== null) as AbstractNode[])
      .sort((a: AbstractNode, b: AbstractNode) => {
        const titleA = this.getItemTitle(a)
        const titleB = this.getItemTitle(b)

        if (titleA === titleB) return 0
        return titleA > titleB ? 1 : -1
      }).sort((a: AbstractNode, b: AbstractNode) => {
        if (a instanceof Entity && b instanceof Entity) {
          const entityTypeA = a.getEntityType().name
          const entityTypeB = b.getEntityType().name

          if (entityTypeA === entityTypeB) return 0
          return entityTypeA > entityTypeB ? 1 : -1
        }
        return 0
      })
  }

  get itemsList () {
    const list: any[] = []
    const groups = this.items.reduce((r: { [key: string]: AbstractNode[] }, a: AbstractNode) => {
      const title = this.getItemTitle(a)
      r[title] = [...r[title] || [], a]
      return r
    }, {})

    for (const key in groups) {
      list.push({ header: key }, ...groups[key])
    }
    return list
  }

  valueComparator (a: any, b: any) {
    if (a instanceof AbstractNode && b instanceof AbstractNode) {
      return a.uuid === b.uuid
    }
    return isEqual(a, b)
  }

  cancelableSearch (query: string) {
    if (this.workingSearch) {
      this.workingSearch.cancel()
    }
    this.workingSearch = new CancelablePromise(search.makeSearch(query))
    this.workingSearch.promise.then(results => {
      search.setResults(results)
    })
    return this.workingSearch
  }

  getItemTitle (item: AbstractNode): string {
    if (item instanceof Layer) {
      return 'Couches cartographiques'
    } else if (item instanceof InfoReglementation) {
      return 'Infos & Règlementations'
    } else if (item instanceof Actualite) {
      return 'Actualités'
    } else if (item instanceof Entity) {
      const parts: string[] = ['Entités géographiques']
      const entityType = item.getEntityType().name
      if (entityType) parts.push(entityType)
      return parts.join(' > ')
    }
    return ''
  }

  getItemText (item: AbstractNode): string {
    for (const source of search.sources) {
      if (item instanceof source) {
        return (item as any)[source.searchField] as string
      }
    }
    return ''
  }

  select (item: AbstractNode): void {
    if (item instanceof Layer) {
      this.selectLayer(item)
    } else if (item instanceof InfoReglementation) {
      const feature = new WKT().readFeature(item.getCommune().geometry.value)
      feature.setProperties({ item: item })
      this.focusFeatureToLayer(InfoReglementation, [feature])
    } else if (item instanceof Actualite) {
      const features: Feature<Geometry>[] = item.getCommunes().map((commune: Commune) => new WKT().readFeature(commune.geometry.value))
      features.forEach((feature) => {
        feature.setProperties({ item: item })
      })
      this.focusFeatureToLayer(Actualite, features)
    } else if (item instanceof Entity) {
      if (!context.activeContext) return
      const layer = context.activeContext.getFakeLayer(Entity.layerId)
      const cb = () => {
        if (layer) {
          layer.enabled = true
        }
        const geom = item.geometry?.value
        if (geom) {
          const feature: Feature<Geometry> = new WKT().readFeature(geom)
          feature.setProperties({ item: item })
          this.focusFeatureToLayer(Entity, [feature])
        }
      }

      if (layer && layer.enabled) {
        cb()
      } else {
        this.cb = cb
        this.dialog = true
      }
    }
  }

  focusFeatureToLayer (model: { layerId: string }, features: Feature<Geometry>[]) {
    if (!context.activeContext) return
    const layer = context.activeContext.getFakeLayer(model.layerId)

    if (layer) {
      const source = (layer.olLayer as VectorLayer<any>).getSource()
      const layerFeatures = source.getFeatures()
      const placedFeatures = intersectionWith(features, layerFeatures, (feature: Feature<Geometry>, layerFeature: Feature<Geometry>) => {
        const layerItem = layerFeature.get('item')
        const item = feature.get('item')
        return layerItem?.uuid === item?.uuid
      })

      if (placedFeatures.length !== features.length) {
        for (const feature of features) {
          if (placedFeatures.indexOf(feature) === -1) {
            source.addFeature(feature)
            placedFeatures.push(feature)
          }
        }
        layer.olLayer.changed()
      }

      map.showClickedFeatures({ features: placedFeatures as FeatureLike[] })
      map.focusOnFeatures(placedFeatures)
    }
  }

  selectLayer (layer: Layer) {
    if (context.activeContext) {
      if (!context.activeContext.layers.find((l: LayerWrapper) => l.model.uuid === layer.uuid)) {
        context.activeContext.layers.push(new LayerWrapper(layer))
      }
    }
  }

  @Watch('search')
  onSearchChanged () {
    if (this.search) {
      this.debounceSearch(this.search)
    }
  }
}
